import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  Image,
  PanResponder,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { captureWebCrop } from '../web/cropCapture.web';
import { Colors, Radius, Shadow, Typography } from '../theme';
import { useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

// Web counterpart to TonyteCropModal.tsx. Identical pan/zoom UI (PanResponder
// and the rest of react-native-web already render this correctly) — the
// ONLY divergence is the final capture step: native's `captureRef(view,
// {format:'jpg', ...})` (react-native-view-shot) has no DOM equivalent, so
// this reconstructs the exact same crop mathematically via
// web/cropCapture.web.ts (object-fit:cover math + Canvas), fed the same
// zoom/offset/frame values the native version's screenshot would have
// captured implicitly. Same `onCrop(uri)` contract — every call site
// (Profile/Cover/Event image flows) is unchanged.
export type CropAspectRatio = '1:1' | '8:5' | '5:2';
export type ImagePreviewKind = 'profile' | 'cover' | 'event';

export type ImagePreviewSession = {
  kind: ImagePreviewKind;
  uri: string;
  aspectRatio: CropAspectRatio;
};

interface TonyteCropModalProps {
  session: ImagePreviewSession;
  onCrop: (uri: string) => void;
  onCancel: () => void;
}

const { width: SW, height: SH } = Dimensions.get('window');
const RATIO_NUM: Record<CropAspectRatio, number> = { '1:1': 1, '8:5': 1.6, '5:2': 2.5 };
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const SLIDER_WIDTH = Math.min(SW - 118, 312);

function computeFrame(ratio: number) {
  const maxW = Math.min(SW - 88, 410);
  const maxH = SH - 360;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) { h = maxH; w = h * ratio; }
  const x = (SW - w) / 2;
  const y = (SH - h) / 2;
  return { w, h, x, y };
}

const TonyteCropModal: React.FC<TonyteCropModalProps> = ({
  session,
  onCrop,
  onCancel,
}) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const { kind, uri, aspectRatio } = session;
  const captureTargetRef = useRef<View>(null);
  const sliderTrackRef = useRef<View>(null);
  const sliderTrackXRef = useRef(0);
  const zoomRef = useRef(kind === 'profile' ? 1.12 : 1.06);
  const ratio = RATIO_NUM[aspectRatio];
  const frame = computeFrame(ratio);
  const isCircle = aspectRatio === '1:1';
  const [zoom, setZoom] = useState(kind === 'profile' ? 1.12 : 1.06);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const [actionsEnabled, setActionsEnabled] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [eventMode, setEventMode] = useState<'fit' | 'crop'>('fit');
  const isEventFit = kind === 'event' && eventMode === 'fit';
  const title =
    kind === 'profile'
      ? t('imageCrop.frameProfile')
      : kind === 'event'
        ? t('imageCrop.frameEvent')
        : t('imageCrop.frameCover');
  const eyebrow = kind === 'profile'
    ? t('imageCrop.profilePhoto')
    : kind === 'event'
      ? t('imageCrop.eventPhoto')
      : t('imageCrop.coverPhoto');

  const maxOffsetX = Math.max(0, (frame.w * (zoom - 1)) / (2 * zoom));
  const maxOffsetY = Math.max(0, (frame.h * (zoom - 1)) / (2 * zoom));
  const zoomProgress = (zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM);

  const clampOffset = useCallback((x: number, y: number) => ({
    x: Math.max(-maxOffsetX, Math.min(maxOffsetX, x)),
    y: Math.max(-maxOffsetY, Math.min(maxOffsetY, y)),
  }), [maxOffsetX, maxOffsetY]);

  const applyZoom = useCallback((nextZoom: number) => {
    const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));
    if (Math.abs(clampedZoom - zoomRef.current) < 0.005) return;
    zoomRef.current = clampedZoom;
    setZoom(Number(clampedZoom.toFixed(2)));
  }, []);

  const setZoomFromPageX = useCallback((pageX: number) => {
    const clamped = Math.max(0, Math.min(SLIDER_WIDTH, pageX - sliderTrackXRef.current));
    const nextZoom = MIN_ZOOM + (clamped / SLIDER_WIDTH) * (MAX_ZOOM - MIN_ZOOM);
    applyZoom(nextZoom);
  }, [applyZoom]);

  const applyOffset = useCallback((nextOffset: { x: number; y: number }) => {
    offsetRef.current = nextOffset;
    setOffset(nextOffset);
  }, []);

  const panResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponderCapture: () => true,
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponderCapture: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: () => {
      dragStartRef.current = offsetRef.current;
    },
    onPanResponderMove: (_event, gesture) => {
      applyOffset(clampOffset(dragStartRef.current.x + gesture.dx, dragStartRef.current.y + gesture.dy));
    },
    onPanResponderTerminationRequest: () => false,
  }), [applyOffset, clampOffset]);

  const sliderResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponderCapture: () => true,
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponderCapture: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: event => {
      sliderTrackRef.current?.measureInWindow((x) => {
        sliderTrackXRef.current = x;
        setZoomFromPageX(event.nativeEvent.pageX);
      });
    },
    onPanResponderMove: event => {
      setZoomFromPageX(event.nativeEvent.pageX);
    },
    onPanResponderTerminationRequest: () => false,
  }), [setZoomFromPageX]);

  const handleUse = useCallback(async () => {
    if (!actionsEnabled) {
      console.log('[Tonyte][ImagePreview] confirm ignored before modal settled', { kind, uri });
      return;
    }
    if (isCapturing) return;
    try {
      setIsCapturing(true);
      console.log('[Tonyte][ImagePreview] crop capture started', { kind, uri, zoom, offset });
      const croppedUri = await captureWebCrop({ uri, frame: { w: frame.w, h: frame.h }, zoom, offset, mode: eventMode });
      console.log('[Tonyte][ImagePreview] modal closed by confirm', { kind, uri, croppedUri });
      onCrop(croppedUri);
    } catch (error) {
      console.log('[Tonyte][ImagePreview] crop capture failed, using original', { kind, uri, error });
      onCrop(uri);
    } finally {
      setIsCapturing(false);
    }
  }, [actionsEnabled, eventMode, isCapturing, kind, offset, onCrop, uri, zoom, frame.w, frame.h]);

  const handleCancel = useCallback(() => {
    if (!actionsEnabled) {
      console.log('[Tonyte][ImagePreview] cancel ignored before modal settled', { kind, uri });
      return;
    }
    console.log('[Tonyte][ImagePreview] modal closed by cancel', { kind, uri });
    onCancel();
  }, [actionsEnabled, kind, onCancel, uri]);

  useEffect(() => {
    console.log('[Tonyte][ImagePreview] modal opened', { kind, uri });
    setActionsEnabled(false);
    const initialZoom = kind === 'profile' ? 1.12 : 1.06;
    zoomRef.current = initialZoom;
    setZoom(initialZoom);
    offsetRef.current = { x: 0, y: 0 };
    dragStartRef.current = { x: 0, y: 0 };
    setOffset({ x: 0, y: 0 });
    setEventMode('fit');
    const actionTimer = setTimeout(() => {
      setActionsEnabled(true);
      console.log('[Tonyte][ImagePreview] modal actions enabled', { kind, uri });
    }, 650);
    return () => {
      clearTimeout(actionTimer);
      console.log('[Tonyte][ImagePreview] modal unmounted', { kind, uri });
    };
  }, [kind, uri]);

  useEffect(() => {
    applyOffset(clampOffset(offsetRef.current.x, offsetRef.current.y));
  }, [applyOffset, clampOffset, zoom]);

  const resetFrame = useCallback(() => {
    const initialZoom = kind === 'profile' ? 1.12 : 1.06;
    zoomRef.current = initialZoom;
    setZoom(initialZoom);
    offsetRef.current = { x: 0, y: 0 };
    dragStartRef.current = { x: 0, y: 0 };
    setOffset({ x: 0, y: 0 });
  }, [kind]);

  return (
    <View style={[styles.root, appearance.root]} pointerEvents="auto">
      <View style={[styles.scrim, appearance.scrim]} />
      <View style={[styles.card, appearance.card]}>
        <View style={styles.header}>
          <View style={[styles.headerIcon, appearance.headerIcon]}>
            <Text style={[styles.headerIconText, appearance.accentSecondaryText]}>◎</Text>
          </View>
          <View style={styles.headerCopy}>
            <Text style={[styles.eyebrow, appearance.textMuted]}>{eyebrow}</Text>
            <Text style={[styles.title, appearance.textPrimary]}>{title}</Text>
            <Text style={[styles.subtitle, appearance.textSecondary]}>{t('imageCrop.previewBeforeSaving')}</Text>
          </View>
        </View>

        <View style={styles.previewArea}>
          <View
            style={[
              styles.previewRing,
              {
                width: frame.w,
                height: frame.h,
                borderRadius: isCircle ? frame.w / 2 : 28,
              },
            ]}
          >
            <View
              style={[
                styles.previewFrame,
                {
                  borderRadius: isCircle ? frame.w / 2 : 26,
                },
              ]}
              ref={captureTargetRef}
              collapsable={false}
              {...(isEventFit ? {} : panResponder.panHandlers)}
            >
              {isEventFit ? <>
                <Image source={{ uri }} style={styles.fitBackdrop} resizeMode="cover" blurRadius={22} />
                <View style={styles.fitShade} />
                <Image source={{ uri }} style={styles.fitImage} resizeMode="contain" />
              </> : <Image
                source={{ uri }}
                style={[
                  styles.img,
                  {
                    transform: [
                      { translateX: offset.x },
                      { translateY: offset.y },
                      { scale: zoom },
                    ],
                  },
                ]}
                resizeMode="cover"
                onLoad={() => console.log('[Tonyte][ImagePreview] preview image loaded', { kind, uri })}
                onError={(error) => console.log('[Tonyte][ImagePreview] preview image failed', { kind, uri, error: error.nativeEvent })}
              />}
            </View>
            <View pointerEvents="none" style={[styles.frameOverlay, { borderRadius: isCircle ? frame.w / 2 : 26 }]} />
          </View>
        </View>

        {kind === 'event' ? <View style={styles.modeSelector}>
          <TouchableOpacity activeOpacity={0.82} style={[styles.modeButton, eventMode === 'fit' ? styles.modeButtonActive : styles.modeButtonInactive]} onPress={() => setEventMode('fit')}><Text style={[styles.modeIcon, eventMode === 'fit' && styles.modeTextActive]}>{eventMode === 'fit' ? '✓' : '▣'}</Text><Text style={[styles.modeText, eventMode === 'fit' && styles.modeTextActive]}>{t('imageCrop.fitEntireImage')}</Text></TouchableOpacity>
          <TouchableOpacity activeOpacity={0.82} style={[styles.modeButton, eventMode === 'crop' ? styles.modeButtonActive : styles.modeButtonInactive]} onPress={() => setEventMode('crop')}><Text style={[styles.modeIcon, eventMode === 'crop' && styles.modeTextActive]}>{eventMode === 'crop' ? '✓' : '⌗'}</Text><Text style={[styles.modeText, eventMode === 'crop' && styles.modeTextActive]}>{t('imageCrop.cropToFill')}</Text></TouchableOpacity>
        </View> : null}
        <Text pointerEvents="none" style={[styles.hint, appearance.textMuted]}>{isEventFit ? t('imageCrop.completeImageHint') : t('imageCrop.dragToReposition')}</Text>

        {!isEventFit ? <View style={styles.zoomRow}>
          <View style={styles.zoomHeader}>
            <Text style={[styles.zoomLabel, appearance.textMuted]}>{t('imageCrop.zoom')}</Text>
            <Text style={[styles.zoomText, appearance.accentSecondaryText]}>{zoom.toFixed(2)}x</Text>
          </View>
          <View style={styles.zoomSlider} {...sliderResponder.panHandlers}>
            <View ref={sliderTrackRef} collapsable={false} style={[styles.zoomTrack, appearance.zoomTrack]}>
              <View style={[styles.zoomTrackFill, appearance.accentBackground, { width: `${zoomProgress * 100}%` }]} />
            </View>
            <View style={[styles.zoomThumb, appearance.zoomThumb, { left: Math.max(0, Math.min(SLIDER_WIDTH - 24, zoomProgress * SLIDER_WIDTH - 12)) }]} />
          </View>
        </View> : null}

        <View style={styles.bottomBar}>
          <TouchableOpacity onPress={resetFrame} hitSlop={12} style={[styles.resetBtn, appearance.secondaryButton]} disabled={isCapturing || !actionsEnabled}>
            <Text style={[styles.cancelText, appearance.textSecondary]}>{t('common.clear')}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleCancel} hitSlop={12} style={[styles.cancelBtn, appearance.secondaryButton]} disabled={isCapturing || !actionsEnabled}>
            <Text style={[styles.cancelText, appearance.textSecondary]}>{t('common.cancel')}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleUse} style={[styles.useBtn, appearance.accentBackground, (isCapturing || !actionsEnabled) && styles.useBtnDim]} hitSlop={12} disabled={isCapturing || !actionsEnabled}>
            <Text style={styles.useText}>{isCapturing ? t('imageCrop.cropping') : t('imageCrop.useImage')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    backgroundColor: 'rgba(0,0,0,0.58)',
    zIndex: 9999,
    elevation: 9999,
  },
  scrim: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0,0,0,0.42)',
  },
  card: {
    width: '100%',
    maxWidth: 430,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(9,14,24,0.96)',
    padding: 18,
    overflow: 'hidden',
    ...Shadow.strong,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(38,208,206,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerIconText: {
    color: Colors.turquoise,
    fontSize: 22,
    fontWeight: Typography.bold,
  },
  headerCopy: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    color: 'rgba(255,255,255,0.42)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },
  title: {
    color: '#fff',
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
    marginTop: 4,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: Typography.xs,
    marginTop: 4,
  },
  previewArea: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  fitBackdrop: { ...StyleSheet.absoluteFillObject, transform: [{ scale: 1.12 }] },
  fitShade: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(4,8,16,0.42)' },
  fitImage: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  modeSelector: { width: '100%', flexDirection: 'row', gap: 10, marginTop: 14 },
  modeButton: { flex: 1, minHeight: 48, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, paddingHorizontal: 10, borderRadius: Radius.pill, borderWidth: 1 },
  modeButtonActive: { backgroundColor: Colors.sunset, borderColor: '#FF9A55', ...Shadow.card },
  modeButtonInactive: { backgroundColor: 'rgba(255,255,255,0.075)', borderColor: 'rgba(255,255,255,0.20)' },
  modeIcon: { color: Colors.turquoise, fontSize: 16, fontWeight: '800' },
  modeText: { color: 'rgba(255,255,255,0.92)', fontSize: 13, fontWeight: '700' },
  modeTextActive: { color: '#FFFFFF' },
  previewRing: {
    overflow: 'hidden',
    padding: 3,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.74)',
    backgroundColor: 'rgba(242,125,38,0.18)',
    shadowColor: Colors.sunset,
    shadowOpacity: 0.36,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
    elevation: 18,
  },
  previewFrame: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: 'rgba(2,6,14,0.82)',
  },
  img: {
    width: '100%',
    height: '100%',
  },
  frameOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.24)',
    backgroundColor: 'transparent',
  },
  zoomRow: {
    marginTop: 12,
  },
  zoomHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  zoomLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  zoomText: {
    color: Colors.turquoise,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  zoomSlider: {
    width: SLIDER_WIDTH,
    height: 42,
    alignSelf: 'center',
    justifyContent: 'center',
  },
  zoomTrack: {
    height: 8,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.12)',
    overflow: 'hidden',
  },
  zoomTrackFill: {
    height: '100%',
    borderRadius: Radius.pill,
    backgroundColor: Colors.sunset,
  },
  zoomThumb: {
    position: 'absolute',
    top: 9,
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#fff',
    backgroundColor: Colors.sunset,
    ...Shadow.glow,
  },
  bottomBar: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    marginTop: 16,
  },
  resetBtn: {
    flex: 0.85,
    paddingVertical: 15,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(255,255,255,0.045)',
    alignItems: 'center',
  },
  cancelBtn: {
    flex: 1,
    paddingVertical: 15,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
  },
  cancelText: {
    color: 'rgba(255,255,255,0.86)',
    fontSize: Typography.base,
    fontWeight: Typography.semibold,
  },
  useBtn: {
    flex: 1,
    backgroundColor: Colors.sunset,
    borderRadius: Radius.pill,
    paddingVertical: 15,
    alignItems: 'center',
    ...Shadow.card,
  },
  useBtnDim: {
    opacity: 0.58,
  },
  useText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: '600',
  },
  hint: {
    textAlign: 'center',
    color: 'rgba(255,255,255,0.44)',
    fontSize: 12,
    marginTop: 10,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  root: daylightStyle(theme, { backgroundColor: theme.colors.overlay }),
  scrim: daylightStyle(theme, { backgroundColor: 'rgba(72,58,44,0.18)' }),
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.elevated,
  }),
  headerIcon: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  zoomTrack: daylightStyle(theme, { backgroundColor: theme.colors.borderStrong }),
  zoomThumb: daylightStyle(theme, { borderColor: theme.colors.surfaceElevated }),
  secondaryButton: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  accentSecondaryText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  accentBackground: daylightStyle(theme, { backgroundColor: theme.colors.accent }),
});

export default TonyteCropModal;
